import {
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    QueryList,
    TemplateRef,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { EstensaSignService } from '../estensasign.service';
import {
    ISeMultiSignInfo,
    ISeSignResultData,
    SeArubaSignInfo,
    SeCompedSignInfo,
    SeConfirmoSignInfo,
    SeInfoCertSignInfo,
    SeMultiSignInfo,
    SeSignInfo,
    SeSignRequestData,
    signInfoIsSet,
    SignType,
    signTypeToNameMap,
} from '../estensasign.model';
import { TranslateService } from '@ngx-translate/core';
import { ModalDialogNotificationService, NotificationService, NotificationType } from '@se/se.ui.html5';
import { BehaviorSubject, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { MatTabChangeEvent } from '@angular/material/tabs';

export const supportedSignTypes: Record<SignType, boolean> = {
    [SignType.Undefined]: false,
    [SignType.CompedDigitalSign]: false,
    [SignType.SISSWay]: false,
    [SignType.SISS]: false,
    [SignType.ARUBA]: false,
    [SignType.InfoCert]: true,
    [SignType.ScrybaSign]: false, // non ancora supportata nel web
    [SignType.Confirmo]: false,
    [SignType.SISSWayCloud]: false, // non ancora supportata nel web
    [SignType.SISSCloud]: false, // non ancora supportata nel web
    [SignType.InsielARSS]: false,
};

@Component({
    selector: 'se-multi-sign-auth-dialog',
    templateUrl: './multi-sign-auth-dialog.component.html',
    styleUrls: ['./multi-sign-auth-dialog.component.scss'],
})
export class MultiSignAuthDialogComponent implements OnDestroy, OnInit {
    @ViewChild('signAuthDialogTemplate')
    signAuthDialogTemplate!: TemplateRef<any>;

    @ViewChildren('pin')
    pinElement!: QueryList<ElementRef>;

    @ViewChildren('otp')
    otpElement!: QueryList<ElementRef>;

    confirmLabel: string = '';
    nextLabel: string = '';

    private signAuthDialog!: MatDialogRef<any>;
    private afterCloseEventSubscription!: Subscription;

    get timeoutMessage() {
        return (
            this.translateService.instant('SignSessionDuration') + ' [' + this.translateService.instant('Minutes') + ']'
        );
    }

    get currentSignType(): SignType {
        return this.signService.currentSignType;
    }

    get isWaiting(): BehaviorSubject<boolean> {
        return this.signService.isWaiting;
    }

    set timeout(value: number) {
        this.signService.timeout = value;
    }

    get timeout(): number {
        return this.signService.timeout;
    }

    // data members
    public signTypeNotSupported: boolean = false;
    public isOnWarning = false;
    public warningMessage = '';

    public isOnError = false;
    errorMessages: string[] = [];
    errorMessage: string = '';
    public data: SeSignInfo[] = [];
    originalSignRequestData: SeSignRequestData[] = [];
    public oneTimePwd: string[] = []; // Temporanea per contenere gli otp che vanno poi mappati sulla struttura dati
    showPin: boolean[] = [];
    showOtp: boolean[] = [];

    selectedTabIndex: number = 0;

    private openSubscription: Subscription;
    private openFromErrorSubscription: Subscription;
    private notifySuccessSubscription: Subscription;
    private notifyFailureSubscription: Subscription;

    // constructor

    constructor(
        public dialog: MatDialog,
        private signService: EstensaSignService,
        private translateService: TranslateService,
        private notificationService: NotificationService,
        private modalNotificationService: ModalDialogNotificationService,
    ) {
        this.confirmLabel = this.translateService.instant('Confirm');
        this.nextLabel = this.translateService.instant('Next');

        this.signTypeNotSupported = !supportedSignTypes[this.currentSignType];
        if (this.signTypeNotSupported) {
            this.isOnWarning = true;
            this.warningMessage =
                SignType[this.currentSignType] + ' - ' + this.translateService.instant('SignTypeNotSupported');
        }

        // Subscription open normale
        this.openSubscription = this.signService.openMultiSignLoginWindowRequired.subscribe(
            (signData: SeSignRequestData[]) => {
                this.originalSignRequestData = signData;
                this.showDialog(signData);
            },
        );

        // Subscription open from error
        this.openFromErrorSubscription = this.signService.openMultiSignLoginWindowFromErrorRequired.subscribe(
            (signData: SeSignRequestData[]) => {
                const signErrorInfo = signData[0];
                this.isOnError = true;
                this.errorMessages = signData.map((sData) => sData.initialErrorMessage || '');
                this.errorMessage = signErrorInfo.initialErrorMessage || '';
                this.showDialog(signData);
            },
        );

        // Subscription Success
        this.notifySuccessSubscription = this.signService.notifyMultiSignSuccess.subscribe((result) => {
            if (this.signAuthDialog) {
                this.signAuthDialog
                    .afterClosed()
                    .pipe(take(1))
                    .subscribe(() => {
                        this._notifySuccess();
                    });
                this.signAuthDialog.close(); // Non tutte le firme hanno la finestra aperta
            } else this._notifySuccess();
        });

        // Subscription Failure
        this.notifyFailureSubscription = this.signService.notifyMultiFailure.subscribe((errorResults) => {
            this.isOnError = true;
            const signError = errorResults[0];
            const errorString = errorResults
                .map((error) => this.translateService.instant(error.serverError || ''))
                .join(','); //temp

            const errorTitle = this.translateService.instant(this.signService.errorMessageKey, { p0: '' }); // Firma Fallita by default
            this.errorMessage = signError.isGenericError
                ? this.translateService.instant(signError.serverError || '')
                : errorTitle;
            this.notificationService.error(this.errorMessage);
            if (!signError.isGenericError) this.handleMultiFailure(errorResults);

            this.notificationService.error(errorString || '', errorTitle); // Aggiungo il dettaglio al messaggio generico nella notifica
        });
    }

    handleMultiFailure = (response: ISeSignResultData[]) => {
        const signError = response.find((res) => res.serverError && !res.signed);
        if (signError) {
            const signerId = signError.signerId;
            this.selectedTabIndex = this.data.findIndex((d) => d.info.signerId === signerId);
            this.data[this.selectedTabIndex].info.pin = '';
            if (this.isOtpRequired()) this.oneTimePwd[this.selectedTabIndex] = '';
            this.errorMessages[this.selectedTabIndex] = signError.serverErrorDetail || '';
            this.data.forEach((userData, idx) => {
                if (userData.info.signerId !== signerId) {
                    this.errorMessages[idx] = '';
                }
            });
        }
    };

    onSelectedTabIndexChange = (changeEvent: MatTabChangeEvent) => {
        const pinElement = this.pinElement.get(changeEvent.index);
        //precedenza in focus al pin
        if (pinElement && !this.data[changeEvent.index].info.pin) {
            setTimeout(() => pinElement.nativeElement.focus(), 250);
        }
        //skip opt se non richiesto dal tipo di firma
        else if (this.isOtpRequired() && !this.oneTimePwd[changeEvent.index]) {
            const otpElement = this.otpElement.get(changeEvent.index);
            if (otpElement) setTimeout(() => otpElement.nativeElement.focus(), 250);
        }
    };

    private _notifySuccess() {
        this.notificationService.success(this.translateService.instant('DigitallySigned'));
        this.modalNotificationService.notify({
            message: this.translateService.instant('SuccessOperation', { p0: '' }),
            title: this.translateService.instant('Information'),
            type: NotificationType.information,
            buttonYes: false,
            buttonNo: false,
            buttonOk: true,
            buttonCancel: false,
        });
    }

    // public functions
    public ngOnInit() {
        this.init();
    }

    ngOnDestroy() {
        this.afterCloseEventSubscription?.unsubscribe();
        this.openSubscription.unsubscribe();
        this.openFromErrorSubscription.unsubscribe();
        this.notifySuccessSubscription.unsubscribe();
        this.notifyFailureSubscription.unsubscribe();
    }

    setNextTabToSign() {
        this.selectedTabIndex = this.data.findIndex((signer) => !signer.info.pin);
    }

    currentIsSet(): boolean {
        return Boolean(this.data[this.selectedTabIndex].info.pin);
    }

    handleConfirmClick() {
        if (this.enableSignButton()) return this.closeModal(false);
        this.setNextTabToSign();
    }

    public closeModal(cancelOps: boolean) {
        if (!cancelOps) {
            // Operazioni preliminari alla firma vera e propria
            this.preSignOps();
            const multiSignData = this.dataToMultiSignFormat();
            // Richiedo firma
            this.signService.endMultiSignFlow(multiSignData);
        } else {
            // Chiudo
            this.signService.cancelSigningFlow();
            this.signAuthDialog.close(true);
        }
    }

    dataToMultiSignFormat = (): SeMultiSignInfo => {
        const multiSignData: ISeMultiSignInfo = {
            signerName: this.data[0].info?.signerName || '',
            signerId: this.data[0].info?.signerId || '',
            pin: this.data[0].info?.pin || '',
            reportIdList: this.originalSignRequestData[0].reportIdList,
            multiSignerInfo: this.data.slice(1).map((seSigInfoData) => ({
                signerName: seSigInfoData.info?.signerName || '',
                signerId: seSigInfoData.info?.signerId || '',
                pin: seSigInfoData.info?.pin || '',
            })),
        };

        if (this.isOtpRequired()) {
            multiSignData.otp = this.oneTimePwd[0];
            multiSignData.multiSignerInfo.forEach(
                (signerInfo, idx) => (signerInfo.otp = this.oneTimePwd[idx + 1] || ''),
            );
        }

        /*un solo tipo di firma e` possibile per ogni documento*/
        const signPropName = signTypeToNameMap[this.data[0].type];
        return {
            type: this.data[0].type,
            [signPropName]: multiSignData,
        };
    };
    private allDataPropsSet = (data: SeSignInfo[]): boolean =>
        data.reduce((acc, signData) => acc && signInfoIsSet(signData.info), true);

    public enableSignButton = () => {
        if (this.signTypeNotSupported) return false;
        switch (this.currentSignType) {
            case SignType.ARUBA:
                return (
                    Boolean(this.data) &&
                    this.allDataPropsSet(this.data) &&
                    this.oneTimePwd.reduce((acc, current) => Boolean(acc && current), true)
                );
            case SignType.InfoCert:
                return Boolean(this.data) && this.allDataPropsSet(this.data);
            case SignType.CompedDigitalSign:
            case SignType.Confirmo:
            case SignType.InsielARSS:
            default:
                return false;
        }
    };

    public isOtpRequired = (): boolean => {
        switch (this.currentSignType) {
            case SignType.ARUBA:
                return true;
            case SignType.CompedDigitalSign:
            case SignType.InfoCert:
            case SignType.Confirmo:
            default:
                return false;
        }
    };

    /*per info cert sembra non servire sulla firma multipla.*/
    public isTimeoutRequired = (): boolean => {
        switch (this.currentSignType) {
            case SignType.ARUBA:
            case SignType.CompedDigitalSign:
            case SignType.InfoCert:
            case SignType.Confirmo:
            case SignType.InsielARSS:
            default:
                return false;
        }
    };

    // private functions
    private showDialog(signData: SeSignRequestData[]) {
        this.data = signData.map((sData) => {
            const tmpSignData = new SeSignInfo();
            tmpSignData.type = this.currentSignType;
            switch (this.currentSignType) {
                case SignType.CompedDigitalSign:
                    tmpSignData.comped = new SeCompedSignInfo(sData);
                    this.timeout = 60; // By default metto il timeout a 60 minuti
                    break;
                case SignType.ARUBA:
                    tmpSignData.aruba = new SeArubaSignInfo(sData);
                    break;
                case SignType.InfoCert:
                    tmpSignData.infoCert = new SeInfoCertSignInfo(sData);
                    break;
                case SignType.Confirmo:
                    tmpSignData.confirmo = new SeConfirmoSignInfo(sData);
                    break;
                default: {
                    this.modalNotificationService.error(
                        this.translateService.instant('Exception_SignNotImplementedFault'),
                    );
                }
            }

            return tmpSignData;
        });

        // Apertura dialog
        this.signAuthDialog = this.dialog.open(this.signAuthDialogTemplate, {
            autoFocus: true,
            height: '35%',
            width: '30%',
            minWidth: '480px',
            minHeight: '425px',
            disableClose: true,
            panelClass: 'outer-sign-dialog-wrapper',
        });

        this.afterCloseEventSubscription = this.signAuthDialog.afterClosed().subscribe(() => {
            this.signService.safeExit(); // Pulizia servizio
            this.init(); // pulizia controller
        });
    }

    private preSignOps = () => {
        /*lascio lo switch per accomodare gli altri tipi di firma in futuro*/
        switch (this.currentSignType) {
            case SignType.ARUBA:
                this.data.forEach((signData, i) => {
                    signData.aruba.otp = this.oneTimePwd[i];
                });
                break;
            default: {
                return;
            }
        }
    };

    private init = () => {
        this.oneTimePwd = [];
        this.isOnError = false;
        this.errorMessage = '';
        this.errorMessages = [];
        if (this.data) this.data.forEach((signData) => signData.info?.resetData());

        if (this.afterCloseEventSubscription) this.afterCloseEventSubscription.unsubscribe();
    };
}
